import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authApi } from '../api/auth';
import { InvitationPreview } from '../types';
import './Auth.css';

const roleLabel = (r: string) =>
  r === 'EMPLOYEE_ADMIN' ? 'administrator integratora'
    : r === 'EMPLOYEE' ? 'pracownik'
    : r === 'CUSTOMER' ? 'klient'
    : r;

const AcceptInvite: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);

  const [password, setPassword] = useState('');
  const [passwordRepeat, setPasswordRepeat] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setLoadError('Brak tokenu zaproszenia.');
      setLoading(false);
      return;
    }
    authApi
      .previewInvitation(token)
      .then(({ data }) => {
        setPreview(data);
        setFirstName(data.firstName || '');
        setLastName(data.lastName || '');
      })
      .catch((e: any) => setLoadError(e?.response?.data?.detail || 'Zaproszenie jest nieprawidłowe lub wygasło.'))
      .finally(() => setLoading(false));
  }, [token]);

  const passwordsMismatch = passwordRepeat.length > 0 && password !== passwordRepeat;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== passwordRepeat) {
      setError('Hasła nie są identyczne — wpisz to samo hasło w obu polach.');
      return;
    }
    if (!acceptedTerms) {
      setError('Potwierdź zapoznanie się z Regulaminem i Polityką prywatności.');
      return;
    }
    setSubmitting(true);
    try {
      await authApi.acceptInvitation(token, {
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      navigate('/login', { replace: true, state: { message: 'Konto aktywowane. Zaloguj się.' } });
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.response?.data?.message || 'Nie udało się aktywować konta.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="auth-page">
        <div className="auth-card auth-card--wide">Ładowanie…</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="auth-page">
        <div className="auth-card auth-card--wide">
          <h1 className="auth-title">Zaproszenie</h1>
          <div className="auth-error">{loadError}</div>
          <p className="auth-footer"><Link to="/login">Przejdź do logowania</Link></p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <form className="auth-card auth-card--wide" onSubmit={submit}>
        <h1 className="auth-title">
          {preview?.roleType === 'CUSTOMER' ? 'Aktywuj konto klienta' : 'Dołącz do zespołu'}
        </h1>
        <p className="auth-subtitle">
          {preview?.roleType === 'CUSTOMER' ? (
            <>Integrator <strong>{preview?.integratorName}</strong> zaprasza Cię do portalu klienta
              jako <strong>{roleLabel(preview?.roleType || '')}</strong>. Ustaw dane logowania.</>
          ) : (
            <>Zaproszono Cię do integratora <strong>{preview?.integratorName}</strong> jako{' '}
              <strong>{roleLabel(preview?.roleType || '')}</strong>. Ustaw dane logowania.</>
          )}
        </p>

        {error && <div className="auth-error">{error}</div>}

        <div className="form-group">
          <label className="form-label">Imię</label>
          <input className="form-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
        </div>
        <div className="form-group">
          <label className="form-label">Nazwisko</label>
          <input className="form-input" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
        </div>
        <div className="form-group">
          <label className="form-label">E-mail (będzie Twoim loginem)</label>
          <input className="form-input" value={preview?.email || ''} disabled readOnly />
        </div>
        <div className="form-group">
          <label className="form-label">Hasło (min. 12 znaków)</label>
          <input className="form-input" type="password" autoComplete="new-password"
            value={password} onChange={(e) => setPassword(e.target.value)} required minLength={12} autoFocus />
        </div>
        <div className="form-group">
          <label className="form-label">Powtórz hasło</label>
          <input className="form-input" type="password" autoComplete="new-password"
            value={passwordRepeat} onChange={(e) => setPasswordRepeat(e.target.value)} required minLength={12} />
          {passwordsMismatch && (
            <span className="item-meta" style={{ color: 'var(--color-danger, #cf3a32)', marginTop: '0.25rem', display: 'block' }}>
              Hasła nie są identyczne.
            </span>
          )}
        </div>

        {/* Dokumenty otwieramy w nowej karcie, żeby wypełniony formularz nie przepadł. */}
        <label className="auth-consent">
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            required
          />
          <span>
            Oświadczam, że zapoznałem się z{' '}
            <Link to="/regulamin" target="_blank" rel="noopener noreferrer">Regulaminem</Link>
            {' '}oraz{' '}
            <Link to="/polityka-prywatnosci" target="_blank" rel="noopener noreferrer">
              Polityką prywatności
            </Link>
            {' '}i akceptuję ich treść.
          </span>
        </label>

        <button className="btn btn-primary auth-btn" type="submit"
          disabled={submitting || password.length < 12 || password !== passwordRepeat || !acceptedTerms}>
          {submitting ? 'Aktywowanie…' : 'Aktywuj konto'}
        </button>

        <p className="auth-footer"><Link to="/login">Masz już konto? Zaloguj się</Link></p>
      </form>
    </div>
  );
};

export default AcceptInvite;
